import cn from 'classnames';
import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@react-md/button';
import { TextField } from '@react-md/form';
import { Typography } from '@react-md/typography';
import { useAppDispatch } from '@app/hooks';
import { authActions } from '@modules/auth';
import { ClipboardButton } from '@components/common-buttons';
import { FeatureContent } from '@components/feature-content';
import { TwoFactorAuthSetupResponse } from '@services/df/auth';
import { useClipboard } from '@hooks';
import { AuthSection, AuthSectionText } from '../components';
import { twoFactorId } from '../helpers';

import styles from '../auth-page.module.scss';

const AuthTwoFactorQrCode = ({ qrCode, secret }: { qrCode: string; secret: string }) => {
    const clipboard = useClipboard(secret);
    const secretKey = (
        <ClipboardButton
            id={twoFactorId('copy_secret_key')}
            theme="primary"
            className="rmd-button--flat"
            {...clipboard}
        >
            {secret}
        </ClipboardButton>
    );

    return (
        <div>
            <AuthSectionText>
                <Trans
                    i18nKey="messages.authenticatorDesc"
                    defaults={
                        'Using an authenticator app like Google Authenticator, scan this QR code or ' +
                        'use the secret key to generate your first passcode.<br/><br/Then enter ' +
                        'the code from your authenticator app to complete setup.'
                    }
                />
            </AuthSectionText>
            <div className={cn('rmd-typography--center', 'micro-margin-top')}>{secretKey}</div>
            <img src={qrCode} alt="qr-code" className={cn(styles.qr)} />
        </div>
    );
};

const AuthTwoFactorRecoveryCodes = ({
    recoveryCodes,
    onSuccess,
}: {
    recoveryCodes: string[];
    onSuccess?: () => void;
}) => {
    const dispatch = useAppDispatch();
    const handleConfirm = useCallback(() => {
        dispatch(authActions.setup2FaCompleted());
        onSuccess?.();
    }, [dispatch, onSuccess]);

    const codes = recoveryCodes.join('\n');
    const clipboard = useClipboard(codes);

    const { t } = useTranslation();

    return (
        <div className={cn('fieldset-grid')}>
            <AuthSectionText>
                {t(
                    'messages.saveCodes',
                    'Save your recovery codes in a safe place. ' +
                        'Your recovery codes can help you get back into your account if you lose access.',
                )}
            </AuthSectionText>

            <div className={cn('fieldset-grid', 'base-paddings', 'items-center', styles.codes)}>
                <Typography type="body-2" color="secondary" className="whitespace-pre-wrap">
                    {recoveryCodes.join(' ')}
                </Typography>
                <ClipboardButton id={twoFactorId('copy_codes')} {...clipboard} />
            </div>

            <FeatureContent
                contentKey={twoFactorId('sign_in')}
                fallback={t('buttons.signIn', 'Sign in')}
                wrapper={props => (
                    <Button
                        className="rmd-button--text-large"
                        theme="primary"
                        themeType="contained"
                        onClick={handleConfirm}
                        {...props}
                    />
                )}
            />
        </div>
    );
};

interface Props {
    isLoading: boolean;
    config?: TwoFactorAuthSetupResponse;
    recoveryCodes?: string[];
    onSuccess?: () => void;
}

export const AuthTwoFactorSetup: React.FC<Props> = ({ isLoading, config, children, recoveryCodes, onSuccess }) => {
    const { qr_code_data_uri, secret } = config || {};

    const { t } = useTranslation();

    return (
        <AuthSection title={t('titles.setUpTwoFactor', 'Set up two-factor authentication')}>
            {recoveryCodes ? (
                <AuthTwoFactorRecoveryCodes recoveryCodes={recoveryCodes} onSuccess={onSuccess} />
            ) : (
                <>
                    {config && <AuthTwoFactorQrCode qrCode={qr_code_data_uri!} secret={secret!} />}
                    {children}
                </>
            )}
        </AuthSection>
    );
};
